"use client";
import KibbeConceptLinks from "@/components/kibbe/KibbeConceptLinks";

import KibbeFrameworkNote from "@/components/kibbe/KibbeFrameworkNote";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  SimpleGrid,
  Group,
  Button,
  Divider,
  Table,
  Image,
  Paper,
  Progress,
  Accordion,
  Anchor,
} from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { trackCtaClick } from "@/lib/analytics";
import { kibbeTypes, kibbeTypeFamilies, kibbeComparisons } from "../kibbe/kibbeData";

const familyDescriptions = {
  dramatic: {
    name: "Dramatic Family",
    description: "Sharp, angular, and vertical. The bold, striking types.",
    color: "#1a1a2e",
  },
  natural: {
    name: "Natural Family",
    description: "Blunt, broad, and relaxed. The effortless, athletic types.",
    color: "#2d4a3e",
  },
  classic: {
    name: "Classic Family",
    description: "Balanced, symmetrical, and moderate. The timeless, refined types.",
    color: "#4a3d2d",
  },
  gamine: {
    name: "Gamine Family",
    description: "Petite, compact, and contrasted. The youthful, energetic types.",
    color: "#3d2d4a",
  },
  romantic: {
    name: "Romantic Family",
    description: "Soft, curved, and sensual. The feminine, lush types.",
    color: "#4a2d3d",
  },
};

const historicalTypeIds = new Set(["classic", "natural", "gamine"]);

export default function KibbeGuideContent({ locale }: { locale?: string }) {
  return (
    <Container size="xl" py={48}>
      <Stack gap={48}>
        {/* Hero */}
        <Box>
          <Text fz="xs" fw={500} tt="uppercase" c="dimmed" mb={8} style={{ letterSpacing: 2 }}>
            Complete Guide
          </Text>
          <Title order={1} fz={{ base: 32, md: 48 }} fw={300} mb={16} style={{ letterSpacing: -1 }}>
            Kibbe Body Types: 10 Current Image Identities
          </Title>
          <Text fz="xl" c="dimmed" maw={700}>
            Explore David Kibbe&apos;s 10 current image identities, the Yin/Yang system,
            and three historical types you may encounter in older guides.
          </Text>
        </Box>
        <KibbeConceptLinks /><KibbeFrameworkNote />

        {/* Quick Navigation */}
        <Paper p="lg" radius="md" withBorder>
          <Text fz="sm" fw={600} mb={12}>
            Jump to:
          </Text>
          <Group gap="xs" wrap="wrap">
            <Button component="a" href="#what-is-kibbe" variant="light" size="xs" color="gray">
              What is Kibbe?
            </Button>
            <Button component="a" href="#yin-yang" variant="light" size="xs" color="gray">
              Yin vs Yang
            </Button>
            <Button component="a" href="#all-types" variant="light" size="xs" color="gray">
              10 Current + 3 Historical
            </Button>
            <Button component="a" href="#find-your-type" variant="light" size="xs" color="gray">
              Find Your Type
            </Button>
          </Group>
        </Paper>

        {/* Types Overview Chart */}
        <Box>
          <Image
            src="/kibbe/guide/kibbe-types-chart.png"
            alt="Historical 13-type Kibbe chart, including Classic, Natural and Gamine alongside the 10 current image identities"
            radius="md"
            fallbackSrc="https://placehold.co/1200x600/f5f5f5/666?text=Kibbe+Types+Chart"
          />
          <Text size="sm" c="dimmed" mt="sm">
            Historical 13-type chart: pure Classic, Natural and Gamine are included for reference.
            The current system has 10 image identities.
          </Text>
        </Box>

        <Divider />

        {/* What is Kibbe */}
        <Box id="what-is-kibbe">
          <Title order={2} fz={24} fw={400} mb={16}>
            What is the Kibbe Body Type System?
          </Title>
          <Text fz="md" c="dimmed" mb={16} style={{ lineHeight: 1.8 }}>
            The Kibbe Body Type System was created by David Kibbe in his 1987 book
            &quot;Metamorphosis.&quot; Unlike traditional body typing systems that focus only on
            proportions (like pear, apple, or hourglass), Kibbe considers your entire physical
            presence—bone structure, facial features, flesh distribution, and overall essence.
          </Text>
          <Text fz="md" c="dimmed" mb={16} style={{ lineHeight: 1.8 }}>
            The current system describes 10 image identities, each with style recommendations
            that honor your natural lines rather than trying to &quot;correct&quot; them. The goal
            isn&apos;t to look taller, shorter, curvier, or straighter—it&apos;s to look like the
            most harmonious version of yourself.
          </Text>
          <Text fz="md" c="dimmed" style={{ lineHeight: 1.8 }}>
            At its core, Kibbe is about understanding your unique balance of Yin (soft, curved,
            delicate) and Yang (sharp, angular, strong) characteristics, and dressing to complement
            that balance.
          </Text>
        </Box>

        <Divider />

        {/* Yin vs Yang */}
        <Box id="yin-yang">
          <Title order={2} fz={24} fw={400} mb={16}>
            Understanding Yin and Yang
          </Title>
          <Text fz="md" c="dimmed" mb={24} style={{ lineHeight: 1.8 }}>
            Every Kibbe type is defined by its position on the Yin/Yang spectrum. Understanding
            these concepts is key to understanding your type.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={32} mb={32}>
            <Paper p="lg" radius="md" withBorder>
              <Text fz="lg" fw={500} mb={12}>
                Yin Characteristics
              </Text>
              <Text fz="sm" c="dimmed" mb={16}>
                Soft, rounded, delicate, small, curved
              </Text>
              <Stack gap={8}>
                <Text fz="sm">• Rounded bone structure</Text>
                <Text fz="sm">• Soft, fleshy curves</Text>
                <Text fz="sm">• Full lips, large eyes</Text>
                <Text fz="sm">• Delicate, sloping shoulders</Text>
                <Text fz="sm">• Short to moderate vertical line</Text>
              </Stack>
            </Paper>
            <Paper p="lg" radius="md" withBorder>
              <Text fz="lg" fw={500} mb={12}>
                Yang Characteristics
              </Text>
              <Text fz="sm" c="dimmed" mb={16}>
                Sharp, angular, strong, long, straight
              </Text>
              <Stack gap={8}>
                <Text fz="sm">• Angular bone structure</Text>
                <Text fz="sm">• Lean, taut flesh</Text>
                <Text fz="sm">• Narrow features, thin lips</Text>
                <Text fz="sm">• Sharp, prominent shoulders</Text>
                <Text fz="sm">• Long vertical line</Text>
              </Stack>
            </Paper>
          </SimpleGrid>

          <Image
            src="/kibbe/guide/yin-yang-spectrum.png"
            alt="Kibbe Yin Yang spectrum visualization"
            radius="md"
            fallbackSrc="https://placehold.co/1200x300/f5f5f5/666?text=Yin+Yang+Spectrum"
          />
        </Box>

        <Divider />

        {/* Current identities and historical references */}
        <Box id="all-types">
          <Title order={2} fz={24} fw={400} mb={8}>
            The 10 Current Identities and 3 Historical Types
          </Title>
          <Text fz="md" c="dimmed" mb={32}>
            Organized by family, from most Yang to most Yin. David Kibbe describes a ten-archetype{" "}
            <Anchor href="https://davidkibbe.co/about/">Image Identity System</Anchor>.
            Pure Classic, Natural and Gamine are historical references, labeled below.
          </Text>

          {Object.entries(kibbeTypeFamilies).map(([family, typeIds]) => (
            <Box key={family} mb={48}>
              <Group gap={12} mb={16}>
                <Box
                  w={4}
                  h={24}
                  style={{
                    backgroundColor: familyDescriptions[family as keyof typeof familyDescriptions].color,
                    borderRadius: 2,
                  }}
                />
                <Box>
                  <Text fz="lg" fw={500}>
                    {familyDescriptions[family as keyof typeof familyDescriptions].name}
                  </Text>
                  <Text fz="sm" c="dimmed">
                    {familyDescriptions[family as keyof typeof familyDescriptions].description}
                  </Text>
                </Box>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2, md: typeIds.length }} spacing={16}>
                {typeIds.map((typeId) => {
                  const type = kibbeTypes[typeId];
                  return (
                    <Paper key={typeId} p="lg" radius="md" withBorder>
                      <Text fz="md" fw={500} mb={4}>
                        {type.name}
                      </Text>
                      <Text fz="xs" fw={500} mb={8}>
                        {historicalTypeIds.has(typeId) ? "Historical reference" : "Current image identity"}
                      </Text>
                      <Text fz="xs" c="dimmed" mb={12}>
                        {type.yinYang}
                      </Text>
                      <Text fz="sm" c="dimmed" mb={16} lineClamp={3}>
                        {type.tagline}
                      </Text>
                      <Text fz="xs" c="dimmed" mb={8}>
                        Celebrities: {type.celebrities.slice(0, 2).join(", ")}
                      </Text>
                      <Button
                        component={Link}
                        href={`/kibbe/${typeId}`}
                        variant="light"
                        size="xs"
                        fullWidth
                        rightSection={<IconChevronRight size={14} />}
                      >
                        Learn more
                      </Button>
                    </Paper>
                  );
                })}
              </SimpleGrid>
            </Box>
          ))}
        </Box>

        <Divider />

        {/* Quick Reference Table */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Quick Reference Chart
          </Title>
          <Box style={{ overflowX: "auto" }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Yin/Yang</Table.Th>
                  <Table.Th>Key Traits</Table.Th>
                  <Table.Th>Height</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Object.values(kibbeTypes).map((type) => (
                  <Table.Tr key={type.id}>
                    <Table.Td>
                      <Link
                        href={`/kibbe/${type.id}`}
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        <Text fz="sm" fw={500}>
                          {type.name}
                        </Text>
                      </Link>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">
                        {historicalTypeIds.has(type.id) ? "Historical reference" : "Current image identity"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" c="dimmed">
                        {type.yinYang}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" c="dimmed">
                        {type.characteristics.bone[0]}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" c="dimmed">
                        {type.family === "gamine"
                          ? "5'5\" and under"
                          : type.family === "dramatic" || type.id === "flamboyant-natural"
                            ? "5'7\" and up"
                            : "Any height"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </Box>

        <Divider />

        {/* How to Find Your Type */}
        <Box id="find-your-type">
          <Title order={2} fz={24} fw={400} mb={16}>
            How to Find Your Kibbe Body Type
          </Title>
          <Text fz="md" c="dimmed" mb={24} style={{ lineHeight: 1.8 }}>
            Finding your Kibbe type can be challenging because it requires seeing yourself
            objectively. Here are the most common approaches:
          </Text>

          <Accordion variant="separated" mb={32}>
            <Accordion.Item value="quiz">
              <Accordion.Control>
                <Text fw={500}>Take a Kibbe Quiz</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed">
                  Traditional Kibbe quizzes ask you to self-assess your features (bone structure,
                  flesh, facial features) and tally up Yin vs Yang answers. The problem? Most people
                  struggle to see themselves objectively, leading to mistyping. That&apos;s why
                  a quiz or AI tool is best treated as a starting point for outfit experiments, rather than a verified identity.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="photos">
              <Accordion.Control>
                <Text fw={500}>Analyze Your Photos</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed">
                  Take full-body photos in fitted, neutral clothing. Look at your silhouette: Is
                  your vertical line long or short? Are your shoulders sharp or rounded? Is your
                  flesh taut or soft? Compare complete outfits; celebrity resemblance cannot verify your type.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="clothes">
              <Accordion.Control>
                <Text fw={500}>The Clothing Test</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed">
                  Try on different style lines. Do you look better in sharp, structured pieces
                  (Yang) or soft, draped ones (Yin)? Do long, unbroken lines elongate you or
                  overwhelm you? Your body will tell you what works—if you know what to look for.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="ai">
              <Accordion.Control>
                <Text fw={500}>AI-Powered Analysis</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed" mb={12}>
                  AI photo analysis can suggest an image identity and styling ideas to explore.
                  Photos, pose and clothing can affect the result; an AI suggestion does not verify
                  your identity or replace David Kibbe&apos;s own consultation.
                </Text>
                <Button
                  component={Link}
                  href="/kibbe-body-type-test"
                  onClick={() => trackCtaClick("kibbe_guide_test_inline")}
                  variant="light"
                  size="xs"
                  rightSection={<IconChevronRight size={14} />}
                >
                  Try the AI Kibbe Test
                </Button>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Box>

        <Divider />

        {/* FAQ */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Frequently Asked Questions
          </Title>
          <Accordion variant="separated">
            <Accordion.Item value="weight">
              <Accordion.Control>
                <Text fw={500}>Does weight affect Kibbe type?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed">
                  No. Your Kibbe type is determined by your bone structure, which doesn&apos;t change
                  with weight. A Dramatic at any weight will still have a long vertical line and
                  angular bones. Weight only affects how flesh sits on your frame.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="age">
              <Accordion.Control>
                <Text fw={500}>Does Kibbe type change with age?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed">
                  Your bone structure doesn&apos;t change, so your fundamental type remains the same.
                  However, flesh may soften with age, which can affect how you style within your
                  type. David Kibbe recommends embracing this natural evolution.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="men">
              <Accordion.Control>
                <Text fw={500}>Does Kibbe work for men?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed">
                  The original system was designed for women, but the Yin/Yang principles apply to
                  men too. Many men find value in understanding whether they&apos;re more angular
                  (Dramatic/Natural) or softer (Romantic/Classic) in their bone structure.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="mixed">
              <Accordion.Control>
                <Text fw={500}>What if I have traits from multiple types?</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Text fz="sm" c="dimmed">
                  Everyone has a mix of Yin and Yang—the identities describe combinations, not just two extremes.
                  Focus on your dominant characteristics. If you&apos;re between types, you&apos;re
                  likely one of the &quot;soft&quot; or &quot;flamboyant&quot; versions that blend
                  characteristics.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Box>

        <Divider />

        {/* CTA */}
        <Box py={32} ta="center">
          <Title order={2} fz={24} fw={400} mb={8}>
            Ready to Find Your Kibbe Type?
          </Title>
          <Text fz="sm" c="dimmed" mb={24} maw={500} mx="auto">
            Explore a suggested Kibbe image identity and personalized styling ideas from your photos.
            Use the result as a starting point for trying outfits.
          </Text>
          <Group justify="center" gap="md" wrap="wrap">
            <Button
              component={Link}
              href="/kibbe-body-type-test"
              onClick={() => trackCtaClick("kibbe_guide_test_bottom")}
              size="xl"
              color="dark"
              rightSection={<IconChevronRight size={18} />}
            >
              Take the AI Kibbe Test
            </Button>
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("kibbe_guide_colors_bottom")}
              size="xl"
              variant="default"
              rightSection={<IconChevronRight size={18} />}
            >
              Prefer colors first? Free AI color analysis
            </Button>
          </Group>
        </Box>

        {/* Common Comparisons - SEO Internal Links */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={8}>
            Confused Between Two Types?
          </Title>
          <Text fz="md" c="dimmed" mb={24}>
            These are the most commonly confused Kibbe types. Read our in-depth comparisons to find your true type.
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 2 }} spacing={12}>
            {kibbeComparisons.map(([type1, type2]) => {
              const t1 = kibbeTypes[type1];
              const t2 = kibbeTypes[type2];
              return (
                <Button
                  key={`${type1}-${type2}`}
                  component={Link}
                  href={`/kibbe/compare/${type1}/${type2}`}
                  variant="default"
                  size="sm"
                  rightSection={<IconChevronRight size={14} />}
                >
                  {t1.name} vs {t2.name}
                </Button>
              );
            })}
          </SimpleGrid>
        </Box>

        <Divider />

        {/* Browse All Types */}
        <Box>
          <Title order={3} fz={18} fw={500} mb={16}>
            Browse Current Identities and Historical Guides
          </Title>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing={12}>
            {Object.entries(kibbeTypes).map(([id, type]) => (
                <Stack key={id} gap={4}>
                  <Button component={Link} href={`/kibbe/${id}`} variant="default" size="sm">
                    {type.name}
                  </Button>
                  <Text size="xs" c="dimmed" ta="center">
                    {historicalTypeIds.has(id) ? "Historical reference" : "Current identity"}
                  </Text>
                </Stack>
            ))}
          </SimpleGrid>
        </Box>

        {/* More Kibbe Guides */}
        <Box>
          <Title order={3} fz={18} fw={500} mb={16}>
            More Kibbe Guides
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing={12}>
            <Button component={Link} href="/kibbe-body-types-for-men" variant="default" size="sm">
              Kibbe Body Types for Men
            </Button>
            <Button component={Link} href="/kibbe-celebrities" variant="default" size="sm">
              Kibbe Celebrities
            </Button>
            <Button component={Link} href="/what-is-gamine" variant="default" size="sm">
              What Is a Gamine?
            </Button>
            <Button component={Link} href="/plus-size-kibbe-body-types" variant="default" size="sm">
              Plus Size Kibbe Guide
            </Button>
            <Button component={Link} href="/kibbe-outfits" variant="default" size="sm">
              Kibbe Outfit Ideas
            </Button>
            <Button
              component={Link}
              href="/kibbe-body-type-test"
              onClick={() => trackCtaClick("kibbe_guide_test_tools")}
              variant="default"
              size="sm"
            >
              Kibbe Body Type Test
            </Button>
          </SimpleGrid>
        </Box>
      </Stack>
    </Container>
  );
}
